import { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, ChefHat } from 'lucide-react';
import PripravaDavky from './PripravaDavky';
import ModDorucovani from './ModDorucovani';
import Navrat from './Navrat';
import Vyjezd from './Vyjezd';
import { AKCENT, CHYBA, Zpet } from './ui';

/**
 * Rozvozový cockpit — celoobrazovkový, spodní lišta aplikace je během něj
 * schovaná (app.jsx). Vlastní horní lištu nemá: každá obrazovka má návrat
 * ve skle u horní hrany (norma UI, „Horní ovládání na mobilu").
 *
 *   - žádná dávka nebo `pripraveno` / `potvrzena` / `predano_ridici` →
 *     Skládání trasy: z tepla rovnou do trasy nad mapou, kontrola krabic,
 *     jedu (jedna obrazovka — první přidaná objednávka dávku založí),
 *   - naloženo (`nalozeno_v`), ještě nevyjel → V autě: jedno velké Vyjíždím
 *     (2026-10-29), samo se spustí po odjezdu od bistra,
 *   - `v_priprave` → kuchyň dávku ještě skládá (starý způsob),
 *   - `na_ceste` → Na cestě,
 *   - `uzavreno` → Zpět v bistru (shrnutí, hotovost, co vrátit).
 *
 * Obnovuje se sám: událost `RozvozCockpitUpdated` na osobním kanálu
 * rozvozce a pro jistotu každých 15 s. Tiché obnovení nepřekresluje
 * spinner — rozvozce by přišel o rozdělanou akci na obrazovce.
 *
 * Návrh obrazovek: artefakt „Rozvoz cockpit – návrh obrazovek" (2026-10-11).
 */
export default function RozvozCockpit({ user, onNavigate, isDark }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const bezi = useRef(false);
    // Tiché obnovení vyžádané během běžícího dotazu se nezahodí — po jeho
    // doběhnutí se načte ještě jednou (fronta max. 1). Jinak by událost,
    // která přišla uprostřed dotazu (např. seřazení trasy po „Vzít"),
    // zůstala bez odezvy až do dalšího pollu.
    const znovu = useRef(false);
    const fetchRef = useRef(null);

    const fetchDavka = useCallback(async ({ tiche = false } = {}) => {
        if (tiche && bezi.current) { znovu.current = true; return; }
        bezi.current = true;
        if (!tiche) {
            setLoading(true);
            setError(null);
        }
        try {
            const r = await fetch('/api/rozvoz/davka-aktivni', { credentials: 'same-origin' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const json = await r.json();
            setData(json);
            setError(null);
        } catch (e) {
            if (!tiche) setError(e?.message || String(e));
        } finally {
            bezi.current = false;
            if (!tiche) setLoading(false);
            if (znovu.current) {
                znovu.current = false;
                fetchRef.current?.({ tiche: true });
            }
        }
    }, []);
    fetchRef.current = fetchDavka;

    // Optimistic update — child voláním přepíše lokální stav bez čekání na
    // refetch (serializeProCockpit volá drahou ETA matici).
    const optimisticUpdate = useCallback((updater) => {
        setData(prev => updater(prev));
    }, []);

    const obnovit = useCallback(() => fetchDavka({ tiche: true }), [fetchDavka]);

    useEffect(() => { fetchDavka(); }, [fetchDavka]);

    // V rozvozu se aplikace nesmí přenačíst kvůli aktualizaci, když hovor
    // pošle aplikaci do pozadí (aplikace/aktualizace.js).
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('fl-rozvoz', { detail: { aktivni: true } }));
        return () => window.dispatchEvent(new CustomEvent('fl-rozvoz', { detail: { aktivni: false } }));
    }, []);

    useEffect(() => {
        const t = setInterval(obnovit, 15000);
        return () => clearInterval(t);
    }, [obnovit]);

    useEffect(() => {
        const zam = user?.zamestnanec_id;
        if (!zam || !window.Echo) return undefined;
        const kanal = `rozvozce.${zam}`;
        const ch = window.Echo.private(kanal);
        ch.listen('.RozvozCockpitUpdated', obnovit);
        // stopListening, ne leave — kanál poslouchá i Moje směna (useAktivniDavka)
        return () => ch.stopListening('.RozvozCockpitUpdated', obnovit);
    }, [user?.zamestnanec_id, obnovit]);

    const zpet = () => onNavigate?.('prehled', { tab: 'moje-smena' });
    const stav = data?.davka?.stav;
    const predJizdou = !data?.davka || ['pripraveno', 'potvrzena', 'predano_ridici'].includes(stav);
    const nalozeno = predJizdou && !!data?.davka?.nalozeno_v;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'var(--surface-base)', color: 'var(--text-primary)',
            display: 'flex', flexDirection: 'column',
        }}>
            {loading && (
                <Center>
                    <Loader2 size={28} className="animate-spin" style={{ color: AKCENT }} />
                </Center>
            )}

            {!loading && error && (
                <Center>
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <p style={{ color: CHYBA, fontWeight: 700 }}>Cockpit se nenačetl: {error}</p>
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}><Zpet onClick={zpet} isDark={isDark} /></div>
                    </div>
                </Center>
            )}

            {/* Jedno místo pro „bez dávky" i přípravu — první přidaná objednávka
                dávku založí a obrazovka (s mapou) přitom zůstane stejná. */}
            {!loading && !error && predJizdou && !nalozeno && (
                <PripravaDavky data={data} onRefresh={fetchDavka} onTiche={obnovit} onZpet={zpet} isDark={isDark} />
            )}

            {!loading && !error && nalozeno && (
                <Vyjezd data={data} onRefresh={obnovit} onZpet={zpet} isDark={isDark} />
            )}

            {!loading && data?.davka && stav === 'v_priprave' && (
                <Center>
                    <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 16 }}>
                        <Zpet onClick={zpet} isDark={isDark} />
                    </div>
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <ChefHat size={34} style={{ color: AKCENT, marginBottom: 10 }} />
                        <div style={{ fontSize: 18, fontWeight: 900 }}>Kuchyň dávku ještě skládá</div>
                        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>
                            Hotovo {data.davka.kds_objednavek_hotovych ?? 0} z {data.davka.kds_objednavek_celkem ?? 0} objednávek
                        </div>
                    </div>
                </Center>
            )}


            {!loading && data?.davka && stav === 'na_ceste' && (
                <ModDorucovani
                    data={data}
                    user={user}
                    onRefresh={obnovit}
                    onReload={fetchDavka}
                    optimisticUpdate={optimisticUpdate}
                    onZpet={zpet}
                    isDark={isDark}
                />
            )}

            {!loading && data?.davka && stav === 'uzavreno' && (
                <Navrat data={data} onHotovo={zpet} />
            )}
        </div>
    );
}

function Center({ children }) {
    return (
        <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--surface-base)', position: 'relative',
        }}>
            {children}
        </div>
    );
}
